/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// react
import { useRef, useEffect, useState } from 'react';
import isNil from 'lodash/isNil';

// mantine
import { Drawer, Box, ScrollArea } from '@mantine/core';
import { isNotEmpty, useForm } from '@mantine/form';

// Styles
import classes from './EditProducer.module.css';

// local components
import { PreviousDrawerButton } from '@components/atoms/PreviousDrawerButton';
import { DefaultModal } from '@components/molecules/DefaultModal';
import { EditProducerForm } from './EditProducerForm/EditProducerForm';

// store
import useStore from '@store/index';

// i18n
import { useTranslation } from 'react-i18next';

// types
import { TFormValuesProducer } from '../../types';
import { REGEX_STRING } from '@config/constants/regex';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const EditProducer = (props: {
	onClick?: () => void;
	onClose: () => void;
	opened: boolean;
}) => {
	/* ******************************** STATES ******************************* */
	const [isOpenModalCancel, setIsOpenModalCancel] =
		useState(false);
	/* ******************************** HOOKS *********************************** */
	const viewport = useRef<HTMLDivElement>(null);
	const { t } = useTranslation();

	const {
		offsetCreationActiveStep,
		setOffsetCreationActiveStep,
		producersSelectedRow,
	} = useStore();

	// Validators
	const form = useForm<TFormValuesProducer>({
		initialValues: {
			name: '',
			organization: null,
			countryCode: null,
			place: '',
			legalIdentifier: '',
			contact: null,
			logo: null,
		},
		validate: {
			name: (value) =>
				REGEX_STRING.test(value)
					? null
					: t('global.nameMincharacter'),
			organization: isNotEmpty(
				t('global.organizationRequired')
			),
			contact: isNotEmpty(t('global.contactRequired')),
			countryCode: isNotEmpty(
				t('global.countryRequired')
			),
			place: (value) =>
				value && REGEX_STRING.test(value)
					? null
					: t('global.placeMincharacter'),
		},
	});

	useEffect(() => {
		if (!isNil(producersSelectedRow)) {
			form.setValues({
				name: producersSelectedRow.name,
				organization:
					producersSelectedRow?.organizationData?.id ||
					'',
				countryCode: `${producersSelectedRow?.location?.countryCode || null}`,
				place: `${producersSelectedRow?.location?.place || null}`,
				contact: `${producersSelectedRow?.contact?.id || null}`,
				...(producersSelectedRow?.logo
					? { logo: producersSelectedRow.logo }
					: {
							logo: null,
						}),
			});

			form.resetDirty();
		}
	}, [producersSelectedRow]);

	useEffect(() => {
		if (
			offsetCreationActiveStep >= 0 &&
			viewport &&
			viewport.current
		) {
			viewport.current!.scrollTo({
				top: 0,
				behavior: 'smooth',
			});
		}
	}, [offsetCreationActiveStep]);

	useEffect(() => {
		setOffsetCreationActiveStep(0);
	}, []);

	/* ******************************** RENDERING ******************************* */
	return (
		<Drawer.Root
			position="right"
			size="100%"
			opened={props.opened}
			onClose={() => {
				setIsOpenModalCancel(true);
			}}
		>
			<Drawer.Overlay />
			<Drawer.Content
				ref={viewport}
				className={classes.root}
			>
				<DefaultModal
					opened={isOpenModalCancel}
					onConfirm={() => {
						props.onClose();
						setIsOpenModalCancel(false);
						form.reset();
					}}
					onClose={() => {
						setIsOpenModalCancel(false);
					}}
					title={t('producers.backToAllProducersSure')}
				/>
				{/** Header */}
				<Drawer.Header className={classes.header}>
					<PreviousDrawerButton
						onClick={() => {
							setIsOpenModalCancel(true);
						}}
					>
						{t('producers.backToAllProducers')}
					</PreviousDrawerButton>
				</Drawer.Header>
				{/** Body */}
				<Drawer.Body className={classes.body}>
					<Box className={classes.bodySpacer} />
					<Box className={classes.bodyContent}>
						{/** Content */}
						<ScrollArea
							viewportRef={viewport}
							className={classes.containerContent}
						>
							<EditProducerForm
								open={props.opened}
								form={form}
								onCancel={() => {
									setIsOpenModalCancel(true);
								}}
								onBack={() => {
									props.onClose();
									form.reset();
								}}
							/>
						</ScrollArea>
					</Box>
					<Box className={classes.bodySpacer} />
				</Drawer.Body>
				{/** Footer */}
				<Box className={classes.containerFooter}>
					<Box className={classes.spacerFooter} />
					<Box className={classes.spacerFooter} />
				</Box>
			</Drawer.Content>
		</Drawer.Root>
	);
};
